import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, FormBuilder, FormGroup, FormArray, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from '../../../services/api.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-add-product',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './add-product.component.html',
  styleUrls: ['./add-product.component.css']
})
export class AddProductComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('productImageInput') productImageInput?: ElementRef<HTMLInputElement>;
  activeTab: 'details' | 'requirements' = 'details';
  categories: any[] = [];
  loading = false;
  productNameAvailable = true;
  checkingName = false;
  errorMessage = '';
  successMessage = '';
  fileInputRefs: { [key: number]: ElementRef<HTMLInputElement> } = {};
  selectedCategory: any = null;
  categoryHasSuppliers = false;
  categorySupplierWarning = '';
  private productNameInput$ = new Subject<string>();
  
  // Product Image Upload
  selectedProductImageFile: File | null = null;
  productImagePreview: string = '';
  productImageUploadError: string = '';
  uploadedProductImageUrl: string = '';
  productImageUploading: boolean = false;

  // Specification File Uploads (track per specification index)
  specificationFileUploads: { [index: number]: {
    file: File | null;
    fileName: string;
    uploading: boolean;
    uploaded: boolean;
    url: string;
    error: string;
  } } = {};

  // Details Form
  detailsForm: FormGroup;
  specificationsFormArray: FormArray;

  // Requirements Form
  requirementsForm: FormGroup;
  quantitiesFormArray: FormArray;

  // Key Type options
  keyTypes = ['Text Field', 'Text Area', 'Bullet Point', 'Link', 'Upload'];

  // Shipping Term options
  shippingTerms = ['DELIVERED', 'FOB', 'CIF'];

  // Indian States list
  indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
    'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
    'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
  ];

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private router: Router
  ) {
    // Initialize Details Form
    this.detailsForm = this.fb.group({
      category: ['', Validators.required],
      name: ['', [Validators.required, Validators.minLength(1)]],
      description: [''],
      image: [''] // Product image is optional
    });

    // Initialize Specifications FormArray
    this.specificationsFormArray = this.fb.array([]);
    this.detailsForm.addControl('specifications', this.specificationsFormArray);

    // Initialize Requirements Form
    this.requirementsForm = this.fb.group({
      quoteSubmissionDueDate: ['', Validators.required],
      shippingTerm: ['', Validators.required],
      // Delivered fields
      addressLine1: ['PLOT NO. 342, GIDC'],
      addressLine2: ['REVENUE SURVEY NO. 573/P'],
      country: ['India'], // Fixed to India
      state: ['Gujarat'],
      city: ['MITHIROHAR-GANDHIDHAM, Kachchh'],
      pincode: ['370240'],
      // FOB/CIF fields
      mappedPort: ['Port of Mundra - India']
    });

    // Initialize Quantities FormArray
    this.quantitiesFormArray = this.fb.array([]);
    this.requirementsForm.addControl('quantities', this.quantitiesFormArray);
  }

  ngOnInit() {
    this.loadCategories();
    this.addSpecification();
    this.addQuantity();
    
    // Set country to India and disable it
    this.requirementsForm.get('country')?.setValue('India');
    this.requirementsForm.get('country')?.disable();
    
    // Watch for category changes to check suppliers
    this.detailsForm.get('category')?.valueChanges.subscribe(categoryId => {
      this.onCategoryChange(categoryId);
    });
    
    // Watch for shipping term changes
    this.requirementsForm.get('shippingTerm')?.valueChanges.subscribe(term => {
      this.updateShippingFields(term);
    });

    // Setup debounced product name checking
    this.productNameInput$.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(name => {
      if (name && name.trim().length > 0) {
        this.checkProductName();
      }
    });
  }

  ngOnDestroy() {
    this.productNameInput$.complete();
  }

  loadCategories() {
    this.apiService.getCategories(1, 1000, '', 'Active').subscribe({
      next: (response) => {
        if (response.categories) {
          this.categories = response.categories.filter((cat: any) => cat.isActive);
        } else {
          this.categories = response.filter((cat: any) => cat.isActive);
        }
        // If a category is already selected, re-check suppliers
        const selectedCategoryId = this.detailsForm.get('category')?.value;
        if (selectedCategoryId) {
          this.onCategoryChange(selectedCategoryId);
        }
      },
      error: (err) => {
        console.error('Error loading categories:', err);
      }
    });
  }

  onCategoryChange(categoryId: string) {
    if (!categoryId) {
      this.selectedCategory = null;
      this.categoryHasSuppliers = false;
      this.categorySupplierWarning = '';
      return;
    }

    // Find the selected category
    this.selectedCategory = this.categories.find(cat => cat._id === categoryId);
    
    if (!this.selectedCategory) {
      // If category not found in loaded list, fetch it
      this.apiService.getCategory(categoryId).subscribe({
        next: (response) => {
          this.selectedCategory = response.category || response;
          this.checkCategorySuppliers();
        },
        error: (err) => {
          console.error('Error loading category:', err);
          this.categoryHasSuppliers = false;
          this.categorySupplierWarning = 'Error loading category details';
        }
      });
    } else {
      this.checkCategorySuppliers();
    }
  }

  checkCategorySuppliers() {
    if (!this.selectedCategory) {
      this.categoryHasSuppliers = false;
      this.categorySupplierWarning = '';
      return;
    }

    // Check if category has suppliers in supplierRefId (preferred) or suppliers (backward compatibility)
    const suppliers = this.selectedCategory.supplierRefId || this.selectedCategory.suppliers || [];
    
    // Handle both populated objects and IDs
    const supplierCount = Array.isArray(suppliers) 
      ? suppliers.filter((s: any) => s !== null && s !== undefined).length
      : 0;
    
    if (supplierCount === 0) {
      this.categoryHasSuppliers = false;
      this.categorySupplierWarning = 'No suppliers are added to this category. Please add suppliers to the category before creating a product.';
    } else {
      this.categoryHasSuppliers = true;
      this.categorySupplierWarning = '';
      // Clear error message if suppliers are found
      if (this.errorMessage && this.errorMessage.includes('No suppliers')) {
        this.errorMessage = '';
      }
    }
  }

  // Product Name Validation
  onProductNameInput() {
    const name = this.detailsForm.get('name')?.value?.trim();
    if (name && name.length > 0) {
      // Emit to debounced stream
      this.productNameInput$.next(name);
    } else {
      this.checkingName = false;
      this.productNameAvailable = true;
    }
  }

  checkProductName() {
    const name = this.detailsForm.get('name')?.value?.trim();
    if (!name || name.length < 1) {
      this.productNameAvailable = true;
      this.checkingName = false;
      return;
    }

    this.checkingName = true;
    this.apiService.checkProductName(name).subscribe({
      next: (response) => {
        this.productNameAvailable = response.available;
        this.checkingName = false;
        if (!response.available) {
          this.detailsForm.get('name')?.setErrors({ notAvailable: true });
        } else {
          // Clear any previous errors
          const nameControl = this.detailsForm.get('name');
          if (nameControl?.hasError('notAvailable')) {
            const errors = { ...nameControl.errors };
            delete errors['notAvailable'];
            nameControl.setErrors(Object.keys(errors).length > 0 ? errors : null);
            nameControl.updateValueAndValidity();
          }
        }
      },
      error: (err) => {
        this.checkingName = false;
        this.productNameAvailable = false;
        this.detailsForm.get('name')?.setErrors({ notAvailable: true });
      }
    });
  }

  // Specifications Management
  get specificationsControls() {
    return this.specificationsFormArray.controls;
  }

  addSpecification() {
    const specGroup = this.fb.group({
      keyType: ['Text Field', Validators.required],
      key: ['', Validators.required],
      value: ['', Validators.required]
    });

    const index = this.specificationsFormArray.length;

    // Watch for keyType changes to update value field
    specGroup.get('keyType')?.valueChanges.subscribe(keyType => {
      const valueControl = specGroup.get('value');
      if (valueControl) {
        valueControl.setValue('');
      }
      
      // If keyType changes from Upload to something else, clean up file upload
      if (keyType !== 'Upload' && this.specificationFileUploads[index]) {
        this.clearSpecificationFile(index);
      }
    });

    this.specificationsFormArray.push(specGroup);
    
    // Initialize file upload tracking for this specification
    this.specificationFileUploads[index] = {
      file: null,
      fileName: '',
      uploading: false,
      uploaded: false,
      url: '',
      error: ''
    };
  }

  removeSpecification(index: number) {
    // Delete uploaded file from S3 if exists
    const fileUpload = this.specificationFileUploads[index];
    if (fileUpload && fileUpload.url) {
      const fileKey = this.extractKeyFromUrl(fileUpload.url);
      if (fileKey) {
        this.apiService.deleteImage(fileKey).subscribe({
          next: () => console.log('Specification file deleted from S3:', fileKey),
          error: (err) => console.error('Error deleting specification file:', err)
        });
      }
    }
    
    // Remove from array
    this.specificationsFormArray.removeAt(index);
    
    // Rebuild file uploads tracking (indices will shift)
    const newFileUploads: { [index: number]: any } = {};
    Object.keys(this.specificationFileUploads).forEach(key => {
      const oldIndex = parseInt(key);
      if (oldIndex < index) {
        newFileUploads[oldIndex] = this.specificationFileUploads[oldIndex];
      } else if (oldIndex > index) {
        newFileUploads[oldIndex - 1] = this.specificationFileUploads[oldIndex];
      }
    });
    this.specificationFileUploads = newFileUploads;
  }

  // Product Image Upload
  onProductImageSelected(event: any) {
    const file = event.target?.files?.[0];
    if (!file) {
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      const errorMsg = 'Please select a valid image file (jpg, jpeg, or png)';
      this.productImageUploadError = errorMsg;
      this.showError(errorMsg);
      this.resetProductImageInput();
      return;
    }

    // Validate file size (10 MB = 10 * 1024 * 1024 bytes)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      const errorMsg = 'File size must be less than 10 MB';
      this.productImageUploadError = errorMsg;
      this.showError(errorMsg);
      this.resetProductImageInput();
      return;
    }

    // Clear previous errors
    this.productImageUploadError = '';
    this.selectedProductImageFile = file;
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.productImagePreview = e.target.result;
    };
    reader.onerror = () => {
      const errorMsg = 'Error reading file. Please try again.';
      this.productImageUploadError = errorMsg;
      this.showError(errorMsg);
      this.resetProductImageInput();
    };
    reader.readAsDataURL(file);

    // Upload image immediately to S3
    this.uploadProductImageToS3(file);
  }

  uploadProductImageToS3(file: File) {
    this.productImageUploading = true;
    this.productImageUploadError = '';

    // Upload new image to S3 (with products folder)
    this.apiService.uploadImage(file, 'products').subscribe({
      next: (response: any) => {
        console.log('Product image upload response received:', response);
        
        // Handle response format: { meta: { status, msg }, data: { location } }
        if (response && response.meta && response.meta.status === true && response.data && response.data.location) {
          const locationUrl = response.data.location;
          console.log('Product image location URL:', locationUrl);
          
          this.uploadedProductImageUrl = locationUrl;
          this.productImageUploading = false;
          this.productImageUploadError = '';
          // Update form control
          this.detailsForm.get('image')?.setValue(locationUrl);
          console.log('Product image uploaded successfully:', locationUrl);
        } else {
          // Handle error response or unexpected format
          const errorMsg = response?.meta?.msg || response?.message || 'Failed to upload product image';
          console.error('Product image upload failed:', errorMsg, response);
          this.productImageUploadError = errorMsg;
          this.showError(errorMsg);
          this.productImageUploading = false;
          this.resetProductImageInput();
        }
      },
      error: (err: any) => {
        console.error('Error uploading product image:', err);
        let errorMessage = 'Error uploading product image. Please try again.';
        
        if (err.error) {
          if (err.error.meta && err.error.meta.msg) {
            errorMessage = err.error.meta.msg;
          } else if (err.error.message) {
            errorMessage = err.error.message;
          }
        }
        
        this.productImageUploadError = errorMessage;
        this.productImageUploading = false;
        this.showError(errorMessage);
        this.resetProductImageInput();
      }
    });
  }

  removeProductImage() {
    // Delete uploaded image from S3 if exists
    if (this.uploadedProductImageUrl) {
      // Extract key from URL
      const imageKey = this.extractKeyFromProductImageUrl(this.uploadedProductImageUrl);
      if (imageKey) {
        this.apiService.deleteImage(imageKey).subscribe({
          next: () => {
            console.log('Product image deleted from S3:', imageKey);
            // Clear all image-related data after successful deletion
            this.clearProductImage();
          },
          error: (err) => {
            console.error('Error deleting product image from S3:', err);
            // Still clear the UI even if S3 deletion fails
            this.clearProductImage();
          }
        });
      } else {
        // If no key found, just clear the UI
        this.clearProductImage();
      }
    } else {
      // If no uploaded URL, just clear the preview
      this.clearProductImage();
    }
  }

  clearProductImage() {
    this.selectedProductImageFile = null;
    this.productImagePreview = '';
    this.uploadedProductImageUrl = '';
    this.productImageUploading = false;
    this.productImageUploadError = '';
    this.detailsForm.get('image')?.setValue('');
    this.resetProductImageInput();
  }

  resetProductImageInput() {
    if (this.productImageInput && this.productImageInput.nativeElement) {
      this.productImageInput.nativeElement.value = '';
    }
    this.selectedProductImageFile = null;
  }

  extractKeyFromProductImageUrl(url: string): string {
    if (!url) return '';
    
    // If it's already a key (no http/https), return as is
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return url;
    }
    
    // Extract key from S3 URL
    try {
      const urlObj = new URL(url);
      // Remove leading slash from pathname
      return urlObj.pathname.substring(1);
    } catch (error) {
      // Fallback: try to extract manually
      const urlParts = url.split('/');
      const bucketIndex = urlParts.findIndex((part: string) => part.includes('.s3.'));
      if (bucketIndex >= 0 && bucketIndex < urlParts.length - 1) {
        return urlParts.slice(bucketIndex + 1).join('/');
      }
      // Last resort: use everything after .com/
      const comIndex = url.indexOf('.com/');
      if (comIndex >= 0) {
        return url.substring(comIndex + 5);
      }
      return '';
    }
  }

  // File Upload for Specification
  onFileSelected(event: any, index: number) {
    const file = event.target?.files?.[0];
    if (!file) {
      return;
    }

    // Validate file type
    const allowedTypes = ['ai', 'cdr', 'pdf', 'png', 'jpeg', 'jpg', 'xlsx', 'xls', 'csv'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !allowedTypes.includes(fileExtension)) {
      const errorMsg = 'Invalid file type. Allowed: ai, cdr, pdf, png, jpeg, jpg, xlsx, xls, csv';
      this.showError(errorMsg);
      if (this.specificationFileUploads[index]) {
        this.specificationFileUploads[index].error = errorMsg;
      }
      // Reset file input
      if (event.target) {
        event.target.value = '';
      }
      return;
    }

    // Validate file size (150 MB)
    const maxSize = 150 * 1024 * 1024; // 150 MB in bytes
    if (file.size > maxSize) {
      const errorMsg = 'File size exceeds 150 MB limit';
      this.showError(errorMsg);
      if (this.specificationFileUploads[index]) {
        this.specificationFileUploads[index].error = errorMsg;
      }
      // Reset file input
      if (event.target) {
        event.target.value = '';
      }
      return;
    }

    // Initialize file upload tracking if not exists
    if (!this.specificationFileUploads[index]) {
      this.specificationFileUploads[index] = {
        file: null,
        fileName: '',
        uploading: false,
        uploaded: false,
        url: '',
        error: ''
      };
    }

    // Clear previous errors
    this.specificationFileUploads[index].error = '';
    this.specificationFileUploads[index].file = file;
    this.specificationFileUploads[index].fileName = file.name;
    this.specificationFileUploads[index].uploading = true;
    this.specificationFileUploads[index].uploaded = false;

    // Delete old file if exists
    const oldUrl = this.specificationFileUploads[index].url;
    if (oldUrl) {
      const oldKey = this.extractKeyFromUrl(oldUrl);
      if (oldKey) {
        this.apiService.deleteImage(oldKey).subscribe({
          next: () => console.log('Old specification file deleted:', oldKey),
          error: (err) => console.error('Error deleting old file:', err)
        });
      }
    }

    // Upload new file
    this.apiService.uploadSpecificationFile(file, 'specifications').subscribe({
      next: (response: any) => {
        console.log('Specification file upload response:', response);
        
        if (response && response.meta && response.meta.status === true && response.data) {
          const fileUrl = response.data.location || response.data.url || response.data.key;
          this.specificationFileUploads[index].url = fileUrl;
          this.specificationFileUploads[index].uploading = false;
          this.specificationFileUploads[index].uploaded = true;
          this.specificationFileUploads[index].error = '';
          
          // Update form control with file URL
          const specControl = this.specificationsFormArray.at(index);
          specControl.get('value')?.setValue(fileUrl);
          
          console.log('Specification file uploaded successfully:', fileUrl);
        } else {
          const errorMsg = response?.meta?.msg || response?.message || 'Failed to upload file';
          this.specificationFileUploads[index].error = errorMsg;
          this.specificationFileUploads[index].uploading = false;
          this.showError(errorMsg);
        }
      },
      error: (err: any) => {
        console.error('Error uploading specification file:', err);
        let errorMessage = 'Error uploading file. Please try again.';
        
        if (err.error) {
          if (err.error.meta && err.error.meta.msg) {
            errorMessage = err.error.meta.msg;
          } else if (err.error.message) {
            errorMessage = err.error.message;
          }
        }
        
        this.specificationFileUploads[index].error = errorMessage;
        this.specificationFileUploads[index].uploading = false;
        this.specificationFileUploads[index].uploaded = false;
        this.showError(errorMessage);
        
        // Reset file input
        if (event.target) {
          event.target.value = '';
        }
      }
    });
  }

  clearSpecificationFile(index: number) {
    const fileUpload = this.specificationFileUploads[index];
    if (fileUpload && fileUpload.url) {
      const fileKey = this.extractKeyFromUrl(fileUpload.url);
      if (fileKey) {
        this.apiService.deleteImage(fileKey).subscribe({
          next: () => console.log('Specification file deleted from S3:', fileKey),
          error: (err) => console.error('Error deleting specification file:', err)
        });
      }
    }
    
    // Clear form control
    const specControl = this.specificationsFormArray.at(index);
    specControl.get('value')?.setValue('');
    
    // Reset file input element
    const fileInput = document.getElementById('specFileInput' + index) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    
    // Reset file upload tracking
    if (this.specificationFileUploads[index]) {
      this.specificationFileUploads[index] = {
        file: null,
        fileName: '',
        uploading: false,
        uploaded: false,
        url: '',
        error: ''
      };
    }
  }

  triggerFileInput(index: number) {
    const fileInput = document.getElementById('specFileInput' + index) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  extractKeyFromUrl(url: string): string {
    if (!url) return '';
    
    // If it's already a key (no http/https), return as is
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return url;
    }
    
    // Extract key from S3 URL
    try {
      const urlObj = new URL(url);
      // Remove leading slash from pathname
      return urlObj.pathname.substring(1);
    } catch (error) {
      // Fallback: try to extract manually
      const urlParts = url.split('/');
      const bucketIndex = urlParts.findIndex((part: string) => part.includes('.s3.'));
      if (bucketIndex >= 0 && bucketIndex < urlParts.length - 1) {
        return urlParts.slice(bucketIndex + 1).join('/');
      }
      // Last resort: use everything after .com/
      const comIndex = url.indexOf('.com/');
      if (comIndex >= 0) {
        return url.substring(comIndex + 5);
      }
      return '';
    }
  }

  // Requirements Tab
  updateShippingFields(term: string) {
    const form = this.requirementsForm;
    
    // Reset all shipping fields, but keep country as India
    form.patchValue({
      addressLine1: 'PLOT NO. 342, GIDC,',
      addressLine2: 'REVENUE SURVEY NO. 573/P',
      state: 'Gujarat',
      city: 'MITHIROHAR-GANDHIDHAM, Kachchh',
      pincode: '370240',
      mappedPort: 'Port of Mundra - India'
    });
    // Ensure country is always India and disabled
    form.get('country')?.setValue('India');
    form.get('country')?.disable();

    // Set validators based on shipping term
    if (term === 'DELIVERED') {
      // form.get('addressLine1')?.setValidators([Validators.required]);
      // form.get('country')?.setValidators([Validators.required]);
      // form.get('state')?.setValidators([Validators.required]);
      // form.get('city')?.setValidators([Validators.required]);
      // form.get('pincode')?.setValidators([Validators.required]);
      // form.get('mappedPort')?.clearValidators();
      form.get('addressLine1')?.disable();
      form.get('addressLine2')?.disable();
      form.get('country')?.disable();
      form.get('state')?.disable();
      form.get('city')?.disable();
      form.get('pincode')?.disable();
      form.get('mappedPort')?.disable();
    } else if (term === 'FOB' || term === 'CIF') {
      // form.get('addressLine1')?.clearValidators();
      // form.get('country')?.setValidators([Validators.required]);
      // form.get('mappedPort')?.setValidators([Validators.required]);
      // form.get('state')?.clearValidators();
      // form.get('city')?.clearValidators();
      // form.get('pincode')?.clearValidators();

       form.get('addressLine1')?.clearValidators();
      form.get('country')?.setValidators([Validators.required]);
      form.get('mappedPort')?.setValidators([Validators.required]);
      form.get('mappedPort')?.disable();
      form.get('state')?.clearValidators();
      form.get('city')?.clearValidators();
      form.get('pincode')?.clearValidators();
      
    }

    // Update validity
    Object.keys(form.controls).forEach(key => {
      form.get(key)?.updateValueAndValidity();
    });
  }

  // Quantities Management
  get quantitiesControls() {
    return this.quantitiesFormArray.controls;
  }

  addQuantity() {
    const quantityGroup = this.fb.group({
      quantity: ['', [Validators.required, Validators.min(1)]],
      uom: ['', Validators.required],
      isMandatory: [true]
    });
    this.quantitiesFormArray.push(quantityGroup);
  }

  removeQuantity(index: number) {
    this.quantitiesFormArray.removeAt(index);
  }

  // Tab Navigation
  switchTab(tab: 'details' | 'requirements') {
    if (tab === 'requirements') {
      // Validate details form before allowing requirements tab
      if (this.detailsForm.invalid) {
        this.detailsForm.markAllAsTouched();
        this.showError('Please fill all required fields in Details tab');
        return;
      }

      // Check product name availability
      if (!this.productNameAvailable) {
        this.showError('Product name is not available. Please choose a different name.');
        return;
      }

      // Check if category has suppliers before allowing to proceed
      if (!this.categoryHasSuppliers) {
        this.showError('No suppliers are added to this category. Please add suppliers to the category before creating a product.');
        return;
      }
    }
    this.activeTab = tab;
  }

  // Submit Product
  submitProduct() {
    if (this.detailsForm.invalid || this.requirementsForm.invalid) {
      this.detailsForm.markAllAsTouched();
      this.requirementsForm.markAllAsTouched();
      this.showError('Please fill all required fields');
      return;
    }

    if (!this.productNameAvailable) {
      this.showError('Product name is not available. Please choose a different name.');
      return;
    }

    // Check if selected category has suppliers
    if (!this.categoryHasSuppliers) {
      this.showError('No suppliers are added to this category. Please add suppliers to the category before creating a product.');
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    // Prepare shipping details based on shipping term
    const shippingTerm = this.requirementsForm.get('shippingTerm')?.value;
    let shippingDetails: any = {
      shippingTerm: shippingTerm
    };

    if (shippingTerm === 'DELIVERED') {
      shippingDetails = {
        shippingTerm: shippingTerm,
        addressLine1: this.requirementsForm.get('addressLine1')?.value,
        addressLine2: this.requirementsForm.get('addressLine2')?.value,
        country: 'India', // Always India
        state: this.requirementsForm.get('state')?.value,
        city: this.requirementsForm.get('city')?.value,
        pincode: this.requirementsForm.get('pincode')?.value
      };
    } else if (shippingTerm === 'FOB' || shippingTerm === 'CIF') {
      shippingDetails = {
        shippingTerm: shippingTerm,
        country: 'India', // Always India
        mappedPort: this.requirementsForm.get('mappedPort')?.value
      };
    }

    // Process specifications - handle bullet points
    const specifications = this.specificationsFormArray.value.map((spec: any) => {
      if (spec.keyType === 'Bullet Point' && spec.value) {
        // Convert bullet point text to formatted string
        const lines = spec.value.split('\n').filter((line: string) => line.trim());
        spec.value = lines.map((line: string) => `• ${line.trim()}`).join('\n');
      } else if (spec.keyType === 'Link' && spec.value && !spec.value.startsWith('http')) {
        // Add http:// if not present
        spec.value = `http://${spec.value}`;
      }
      return spec;
    });

    const productData = {
      name: this.detailsForm.get('name')?.value.trim(),
      description: this.detailsForm.get('description')?.value || '',
      image: this.uploadedProductImageUrl || '', // Include product image URL (optional)
      category: this.detailsForm.get('category')?.value,
      specifications: specifications,
      quoteSubmissionDueDate: this.requirementsForm.get('quoteSubmissionDueDate')?.value,
      shippingDetails: shippingDetails,
      quantities: this.quantitiesFormArray.value
    };

    this.apiService.createProduct(productData).subscribe({
      next: (response) => {
        
        this.showSuccess('Product created successfully!');
        setTimeout(() => {
          this.loading = false;
          this.router.navigate(['/product']);
        }, 2000);
      },
      error: (err) => {
        this.loading = false;
        this.showError(err.error?.message || 'Error creating product');
      }
    });
  }

  showError(message: string) {
    this.errorMessage = message;
    this.successMessage = '';
    setTimeout(() => {
      this.errorMessage = '';
    }, 5000);
  }

  showSuccess(message: string) {
    this.successMessage = message;
    this.errorMessage = '';
    setTimeout(() => {
      this.successMessage = '';
    }, 5000);
  }

  cancel() {
    // Delete uploaded product image if exists
    if (this.uploadedProductImageUrl) {
      const imageKey = this.extractKeyFromProductImageUrl(this.uploadedProductImageUrl);
      if (imageKey) {
        this.apiService.deleteImage(imageKey).subscribe({
          next: () => console.log('Product image deleted due to cancellation'),
          error: (err) => console.error('Error deleting product image:', err)
        });
      }
    }
    
    // Delete all uploaded specification files
    Object.keys(this.specificationFileUploads).forEach(key => {
      const index = parseInt(key);
      const fileUpload = this.specificationFileUploads[index];
      if (fileUpload && fileUpload.url) {
        const fileKey = this.extractKeyFromUrl(fileUpload.url);
        if (fileKey) {
          this.apiService.deleteImage(fileKey).subscribe({
            next: () => console.log('Specification file deleted due to cancellation:', fileKey),
            error: (err) => console.error('Error deleting specification file:', err)
          });
        }
      }
    });
    
    this.router.navigate(['/product']);
  }

  openDatePicker(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input && input.type === 'date') {
      // Stop event propagation to prevent any parent handlers
      event.stopPropagation();
      // Try to open the date picker if the browser supports it
      if ('showPicker' in HTMLInputElement.prototype) {
        try {
          input.showPicker();
        } catch (err) {
          // If showPicker fails, just focus the input (browser will show picker on focus)
          input.focus();
        }
      } else {
        // Fallback: just focus the input
        input.focus();
      }
    }
  }

  preventManualDateInput(event: KeyboardEvent | Event) {
    if (event instanceof KeyboardEvent) {
      const key = event.key;
      // Allow navigation and special keys
      const allowedKeys = [
        'Tab', 'Escape', 'Enter',
        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
        'Home', 'End', 'PageUp', 'PageDown',
        'Delete', 'Backspace'
      ];
      
      // Prevent Ctrl/Cmd combinations that could be used for typing
      if ((event.ctrlKey || event.metaKey) && (key === 'v' || key === 'V')) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      
      // Prevent all character keys (manual typing)
      // Allow only navigation and special keys
      if (!allowedKeys.includes(key) && key.length === 1) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  }

  getTodayDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}

